import { useCallback, useMemo } from 'react'

import { EventRegistration } from '@tribeplatform/gql-client/types'
import { useRouter } from '@tribeplatform/react-sdk'
import {
  useEventRegistrations,
  useExportEventRegistrations,
} from '@tribeplatform/react-sdk/hooks'
import { simplifyGlobalPaginatedResult } from '@tribeplatform/react-sdk/utils'
import { Button } from '@tribeplatform/react-ui-kit/Button'
import { Modal } from '@tribeplatform/react-ui-kit/Modal'
import { toast } from '@tribeplatform/react-ui-kit/Toast'

import { MemberAvatar } from '../common/components/member/index.js'
import { dayjs } from '../common/lib/dayjs.js'
import { getLocalizedDateFormat } from '../common/utils/date.js'
import { T } from '../i18n/index.js'
import { useI18n } from '../i18n/providers/I18nProvider.js'

import { ShowMoreButton } from './List/components/ShowMoreButton.js'

interface Props {
  eventId: string
  open: boolean
  onClose: () => void
  eventTitle: string
  capacity: number
}

function convertToCSV(data: EventRegistration[]): string {
  if (data.length === 0) {
    return ''
  }
  const headers = ['name', 'email', 'registered at']
  const rows = data.map(item =>
    [
      item.member.name,
      item.member.email,
      new Date(item.registeredAt).toDateString(),
    ].join(','),
  )
  return [headers.join(','), ...rows].join('\n')
}

export const EventRsvpModal = ({
  eventId,
  open,
  onClose,
  eventTitle,
  capacity,
}: Props) => {
  const { $t } = useI18n()
  const { Link } = useRouter()

  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useEventRegistrations({
      variables: { eventId, limit: 10 },
      fields: {
        member: {
          profilePicture: {
            onImage: {
              urls: 'basic',
            },
          },
        },
      },
      useInfiniteQueryOptions: {
        enabled: !!eventId && open,
      },
    })
  const totalCount = data?.pages?.[0]?.totalCount || 0

  const onErrorExport = useCallback(() => {
    toast({
      title: $t({
        id: 'Generics.Error',
        defaultMessage: 'Error',
      }),
      description: $t({
        id: 'Event.RSVPs.Export.Error',
        defaultMessage: 'Failed to export',
      }),
      status: 'error',
    })
  }, [$t])

  const { exportCSV, isLoading: isLoadingExport } = useExportEventRegistrations(
    {
      variables: {
        eventId,
      },
      onError: onErrorExport,
      onSuccess: allRegistrations => {
        const csv = convertToCSV(allRegistrations)
        const blob = new Blob([csv], { type: 'text/csv' })
        const url = URL.createObjectURL(blob)

        const a = document.createElement('a')
        a.href = url
        a.download = 'event-registrations.csv'
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        URL.revokeObjectURL(url)
      },
    },
  )

  const items = useMemo(
    () => simplifyGlobalPaginatedResult<EventRegistration>(data).nodes,
    [data],
  )
  const { localeCode } = useI18n()

  const progress = capacity > 0 ? Math.round((totalCount / capacity) * 100) : 0

  return (
    <Modal open={open} onClose={onClose} size="3xl" showCloseBtn>
      <Modal.Header
        title={$t({
          id: 'Event.RSVPs.Title',
          defaultMessage: 'Event RSVPs',
        })}
        description={$t(
          {
            id: 'Event.RSVPs.Manage.Description',
            defaultMessage: 'Manage RSVPs for {eventTitle}',
          },
          { eventTitle: <span className="font-bold">{eventTitle}</span> },
        )}
      />
      <Modal.Content>
        {isLoading ? (
          <div>
            <div className="bg-action-neutral p-4 rounded-base mb-4">
              <div className="flex items-center justify-between">
                <div className="gap-3 flex flex-col">
                  <div className="h-4 bg-skeleton rounded-full w-28" />
                  <div className="h-4 bg-skeleton rounded-full w-20" />
                </div>
                <div className="gap-3 flex flex-col items-end">
                  <div className="h-4 bg-skeleton rounded-full w-24" />
                  <div className="h-4 bg-skeleton rounded-full w-20" />
                </div>
              </div>
              <div className="h-4 bg-skeleton rounded-full w-full mt-4" />
            </div>
            {Array.from({ length: 5 }).map((_, index) => (
              <div
                // eslint-disable-next-line react/no-array-index-key
                key={index}
              >
                <div className="flex items-center justify-between my-4">
                  <div className="flex items-center gap-2">
                    <div className="h-8  w-8 bg-skeleton rounded-full" />
                    <div className="flex flex-col gap-y-2">
                      <div className="bg-skeleton h-4 w-32 rounded-full" />
                      <div className="bg-skeleton h-4 w-12 rounded-full" />
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-y-2">
                    <div className="bg-skeleton h-4 w-8 rounded-full" />
                    <div className="bg-skeleton h-4 w-12 rounded-full" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div>
            {capacity > 0 && (
              <div className="bg-action-neutral p-4 rounded-base">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-bold text-heading-md mb-2">
                      <T
                        id="Event.RSVPs.CapacityRatio"
                        defaultMessage="{totalCount} / {capacity}"
                        values={{ totalCount, capacity }}
                      />
                    </div>
                    <div>
                      <T
                        id="Event.RSVPs.Confirmed"
                        defaultMessage="RSVPs confirmed"
                      />
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-bold text-heading-md mb-2">
                      <T
                        id="Event.RSVPs.Progress"
                        defaultMessage="{progress}%"
                        values={{ progress }}
                      />
                    </div>
                    <div>
                      <T
                        id="Event.RSVPs.Capacity"
                        defaultMessage="capacity filled"
                      />
                    </div>
                  </div>
                </div>
                <div className="relative w-full bg-action-primary-disabled rounded mt-4 group">
                  <div
                    className="h-2 text-sm text-center rounded bg-decorative-content-7"
                    style={{
                      width: `${progress}%`,
                    }}
                  >
                    <span
                      className="z-10 absolute bg-background p-2 rounded-base bottom-4 group-hover:visible invisible"
                      style={{ left: `${progress}%` }}
                    >
                      <T
                        id="Event.RSVPs.Progress"
                        defaultMessage="{progress}%"
                        values={{ progress }}
                      />
                    </span>
                  </div>
                </div>
              </div>
            )}

            <div className="flex items-center justify-between flex-wrap gap-2 mt-4">
              <div className="font-semibold">
                <T
                  id="Event.RSVPs.AllAttendees"
                  defaultMessage="All Attendees"
                />
              </div>

              <div className="flex items-center flex-wrap gap-2">
                <div>
                  <Button
                    loading={isLoadingExport}
                    onClick={exportCSV}
                    variant="secondary"
                  >
                    <T id="Event.RSVPs.ExportAll" defaultMessage="Export all" />
                  </Button>
                </div>
              </div>
            </div>

            <div className="pt-4">
              {items.map(({ member, id, registeredAt }) => (
                <div key={id} className="my-1">
                  <Link
                    href={`/member/${member?.id}`}
                    className="p-2 rounded-base flex items-center justify-between gap-x-2 break-words min-w-0 hover:bg-surface-hovered"
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <MemberAvatar member={member} size="2.5x" />
                      <div className="min-w-0">
                        <div className="font-bold truncate">{member.name}</div>
                        <div className="text-sm text-content-subdued truncate min-w-0">
                          {member.email}
                        </div>
                      </div>
                    </div>
                    <div className="text-right min-w-20">
                      <div className="text-sm">
                        {dayjs.utc(registeredAt).fromNow()}
                      </div>
                      <div className="text-xs text-content-muted">
                        {getLocalizedDateFormat(registeredAt, localeCode).date}
                      </div>
                    </div>
                  </Link>
                </div>
              ))}

              <ShowMoreButton
                fetchNextPage={fetchNextPage}
                hasNextPage={hasNextPage}
                isFetchingNextPage={isFetchingNextPage}
                title={$t({
                  defaultMessage: 'Load more RSVPs',
                  id: 'Event.RSVPs.LoadMore',
                })}
              />
            </div>
          </div>
        )}
      </Modal.Content>
    </Modal>
  )
}
